import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Card,
  CardHeader,
  CardTitle,
  Button,
  Input,
  IconButton,
  MagnifyingGlassIcon,
  PlusIcon,
  EllipsisHorizontalIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  ChartBarIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  BanknotesIcon,
  cn 
} from '../../components/ui';
import { Customer } from '../../types/campaign';
import { ApiService } from '../../services/apiService';
import { CampaignsTable } from './components/CampaignsTable';
import { AddCustomerModal } from '../customers/AddCustomerModal';
import { 
  Rocket, 
  Search, 
  Plus, 
  Filter,
  Download,
  MoreVertical,
  TrendingUp,
  Users,
  Mail,
  DollarSign,
  Target,
  AlertCircle,
  ArrowRight,
  Play,
  Pause,
  BarChart3,
  Calendar,
  Clock,
  Zap,
  Activity,
  Edit,
  Trash2,
  Copy,
  Settings,
  ChevronDown,
  ExternalLink,
  MessageSquare,
  Sparkles,
  Award,
  CheckCircle,
  XCircle
} from 'lucide-react';

interface BackendCampaign {
  id: string;
  name: string;
  description: string;
  customer_id: string;
  customer_name?: string;
  type: 'email' | 'linkedin' | 'cold_call' | 'sms' | 'multi_channel';
  objective: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  tags: string[];
  total_budget: number;
  spent_budget: number;
  currency: string;
  start_date: string;
  end_date: string;
  target_criteria: string;
  target_size: number;
  target_segments: string[];
  emails_sent: number;
  emails_opened: number;
  emails_clicked: number;
  emails_replied: number;
  linkedin_requests: number;
  linkedin_accepted: number;
  calls_made: number;
  calls_answered: number;
  sms_sent: number;
  sms_replied: number;
  revenue: number;
  customer_payment: number;
  payment_schedule: 'one_time' | 'monthly' | 'milestone_based' | 'performance_based';
  payment_status: 'pending' | 'partial' | 'paid' | 'overdue';
  created_at: string;
  updated_at: string;
}

const statusConfig = {
  draft: { 
    label: 'Draft', 
    color: 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300',
    dot: 'bg-neutral-500',
    icon: <PencilIcon className="w-3 h-3" />
  },
  active: { 
    label: 'Active', 
    color: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400',
    dot: 'bg-emerald-500',
    icon: <PlayIcon className="w-3 h-3" />
  },
  paused: { 
    label: 'Paused', 
    color: 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400',
    dot: 'bg-amber-500',
    icon: <PauseIcon className="w-3 h-3" />
  },
  completed: { 
    label: 'Completed', 
    color: 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400',
    dot: 'bg-blue-500',
    icon: <CheckCircleIcon className="w-3 h-3" />
  },
  archived: { 
    label: 'Archived', 
    color: 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400',
    dot: 'bg-neutral-400',
    icon: <StopIcon className="w-3 h-3" />
  }
};

const priorityConfig = {
  low: { color: 'text-neutral-600 dark:text-neutral-400', bg: 'bg-neutral-50 dark:bg-neutral-800' },
  medium: { color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/10' },
  high: { color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/10' },
  urgent: { color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/10' },
};

const typeConfig = {
  email: { label: 'Email', icon: '📧', color: 'bg-blue-500' },
  linkedin: { label: 'LinkedIn', icon: '💼', color: 'bg-indigo-500' },
  cold_call: { label: 'Cold Call', icon: '📞', color: 'bg-green-500' },
  sms: { label: 'SMS', icon: '💬', color: 'bg-purple-500' },
  multi_channel: { label: 'Multi-Channel', icon: '🚀', color: 'bg-gradient-to-r from-purple-500 to-pink-500' },
};

const paymentStatusConfig = {
  pending: { color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/10' },
  partial: { color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/10' },
  paid: { color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/10' },
  overdue: { color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/10' },
};

const ProgressRing: React.FC<{ 
  progress: number; 
  size?: number; 
  strokeWidth?: number; 
  color?: string;
  showPercentage?: boolean;
}> = ({ 
  progress, 
  size = 60, 
  strokeWidth = 6,
  color = '#3b82f6',
  showPercentage = true
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg
        className="transform -rotate-90"
        width={size}
        height={size}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          className="text-neutral-200 dark:text-neutral-700"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{
            transition: 'stroke-dashoffset 0.8s ease-in-out',
          }}
        />
      </svg>
      {showPercentage && (
        <div className="absolute text-sm font-bold text-neutral-700 dark:text-neutral-200">
        {Math.round(progress)}%
      </div>
      )}
    </div>
  );
};

const MetricCard: React.FC<{
  title: string;
  value: string | number;
  change: string;
  icon: React.ElementType;
  gradient: string;
  description?: string;
  decimals?: number;
}> = ({ title, value, change, icon: Icon, gradient, description, decimals = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className="group relative p-6 bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl hover:border-neutral-600/50 transition-all duration-300 shadow-xl hover:shadow-2xl"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-neutral-400 text-sm font-medium mb-2">{title}</p>
          <p className="text-3xl font-bold text-white mb-1">
            {typeof value === 'number' ? (
              <AnimatedCounter end={value} decimals={decimals} />
            ) : (
              value
            )}
          </p>
          <div className="flex items-center space-x-2">
            <span className="text-green-400 text-sm font-medium">{change}</span>
            <TrendingUp className="w-4 h-4 text-green-400" />
          </div>
          {description && (
            <p className="text-neutral-500 text-xs mt-2">{description}</p>
          )}
        </div>
        <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", gradient)}>
          <Icon className="w-6 h-6 text-white" />
            </div>
        </div>
      
      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.div>
  );
};

const CampaignCard: React.FC<{
  campaign: BackendCampaign;
  onStatusChange: (id: string, status: BackendCampaign['status']) => void;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
}> = ({ campaign, onStatusChange, onView, onEdit, onDuplicate, onDelete }) => {
  const [showActions, setShowActions] = useState(false);
  
  const openRate = campaign.emails_sent > 0 ? (campaign.emails_opened / campaign.emails_sent) * 100 : 0;
  const replyRate = campaign.emails_sent > 0 ? (campaign.emails_replied / campaign.emails_sent) * 100 : 0;
  const budgetProgress = campaign.total_budget > 0 ? (campaign.spent_budget / campaign.total_budget) * 100 : 0;
  
  const getStatusAction = () => {
    switch (campaign.status) {
      case 'draft':
        return { label: 'Start', action: () => onStatusChange(campaign.id, 'active'), icon: <PlayIcon className="w-4 h-4" /> };
      case 'active':
        return { label: 'Pause', action: () => onStatusChange(campaign.id, 'paused'), icon: <PauseIcon className="w-4 h-4" /> };
      case 'paused':
        return { label: 'Resume', action: () => onStatusChange(campaign.id, 'active'), icon: <PlayIcon className="w-4 h-4" /> };
      default:
        return null;
    }
  };

  const statusAction = getStatusAction();

    return (
      <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, boxShadow: "0 12px 25px -5px rgba(0, 0, 0, 0.15)" }}
      transition={{ duration: 0.2 }}
    >
      <Card className="p-6 border border-neutral-200 dark:border-neutral-700 hover:border-primary-300 dark:hover:border-primary-600 transition-all duration-200">
          {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-3 mb-2">
              <div className={cn(
                "w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg shadow-sm",
                typeConfig[campaign.type].color
              )}>
                {typeConfig[campaign.type].icon}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                  {campaign.name}
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  {campaign.customer_name || 'Unknown Customer'}
                </p>
              </div>
          </div>

            <div className="flex items-center space-x-3 mb-3">
              <span className={cn(
                'inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium',
                statusConfig[campaign.status].color
              )}>
                {statusConfig[campaign.status].icon}
                <span>{statusConfig[campaign.status].label}</span>
              </span>
              
              <span className={cn(
                'px-2 py-1 rounded text-xs font-medium',
                priorityConfig[campaign.priority].color,
                priorityConfig[campaign.priority].bg
              )}>
                {campaign.priority.charAt(0).toUpperCase() + campaign.priority.slice(1)} Priority
                </span>
              
              <span className={cn(
                'px-2 py-1 rounded text-xs font-medium',
                paymentStatusConfig[campaign.payment_status].color,
                paymentStatusConfig[campaign.payment_status].bg
              )}>
                Payment: {campaign.payment_status.charAt(0).toUpperCase() + campaign.payment_status.slice(1)}
              </span>
                </div>
              </div>
              
          <div className="relative">
            <IconButton
              icon={<EllipsisHorizontalIcon />}
              variant="ghost"
              size="sm"
              onClick={() => setShowActions(!showActions)}
              aria-label="More actions"
            />
            
            <AnimatePresence>
              {showActions && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 z-10"
                >
                  <div className="py-1">
                    <button
                      onClick={() => { onView(campaign.id); setShowActions(false); }}
                      className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      <EyeIcon className="w-4 h-4" />
                      <span>View Details</span>
                    </button>
                    <button
                      onClick={() => { onEdit(campaign.id); setShowActions(false); }}
                      className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      <PencilIcon className="w-4 h-4" />
                      <span>Edit Campaign</span>
                    </button>
                    <button
                      onClick={() => { onDuplicate(campaign.id); setShowActions(false); }}
                      className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                      <span>Duplicate</span>
                    </button>
                    <div className="border-t border-neutral-200 dark:border-neutral-600 my-1"></div>
                    <button
                      onClick={() => { onDelete(campaign.id); setShowActions(false); }}
                      className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <TrashIcon className="w-4 h-4" />
                      <span>Delete</span>
                    </button>
        </div>
        </motion.div>
              )}
            </AnimatePresence>
          </div>
      </div>

        {/* Description */}
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 line-clamp-2">
          {campaign.description}
        </p>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs text-neutral-500">Open Rate</span>
              <span className="text-xs font-medium text-neutral-700 dark:text-neutral-200">
                {openRate.toFixed(1)}%
              </span>
        </div>
            <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(openRate, 100)}%` }}
              />
            </div>
          </div>
          
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs text-neutral-500">Reply Rate</span>
              <span className="text-xs font-medium text-neutral-700 dark:text-neutral-200">
                {replyRate.toFixed(1)}%
                </span>
            </div>
            <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
              <div 
                className="bg-emerald-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(replyRate, 100)}%` }}
                />
              </div>
            </div>
          </div>

        {/* Budget Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Budget Progress</span>
            <span className="text-sm text-neutral-500">
              ${campaign.spent_budget.toLocaleString()} / ${campaign.total_budget.toLocaleString()}
                            </span>
                          </div>
          <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
            <div 
              className={cn(
                "h-2 rounded-full transition-all duration-500",
                budgetProgress >= 90 ? "bg-red-500" : budgetProgress >= 70 ? "bg-amber-500" : "bg-blue-500"
              )}
              style={{ width: `${Math.min(budgetProgress, 100)}%` }}
            />
                          </div>
                        </div>

        {/* Revenue and Payment Info */}
        <div className="flex items-center justify-between mb-4 p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                        <div className="text-center">
            <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
              ${campaign.revenue.toLocaleString()}
            </div>
            <div className="text-xs text-neutral-500">Revenue Generated</div>
                        </div>
                        <div className="text-center">
            <div className="text-sm font-bold text-blue-600 dark:text-blue-400">
              ${campaign.customer_payment.toLocaleString()}
            </div>
            <div className="text-xs text-neutral-500">Customer Payment</div>
                        </div>
                        <div className="text-center">
            <div className="text-sm font-bold text-purple-600 dark:text-purple-400">
              {((campaign.customer_payment - campaign.spent_budget) / campaign.customer_payment * 100).toFixed(1)}%
                        </div>
            <div className="text-xs text-neutral-500">Profit Margin</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm"
              leftIcon={<ChartBarIcon />}
              onClick={() => onView(campaign.id)}
            >
              Analytics
                          </Button>
            {statusAction && (
                          <Button 
                variant={campaign.status === 'active' ? 'outline' : 'primary'}
                            size="sm"
                leftIcon={statusAction.icon}
                onClick={statusAction.action}
              >
                {statusAction.label}
                          </Button>
            )}
                  </div>

          <div className="flex items-center space-x-1 text-xs text-neutral-500">
            <ClockIcon className="w-3 h-3" />
            <span>
              {new Date(campaign.updated_at).toLocaleDateString()}
                                        </span>
                                      </div>
                                    </div>
      </Card>
    </motion.div>
  );
};

interface LocalCampaign {
  id: string;
  name: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  type: 'email' | 'linkedin' | 'multi-channel';
  totalContacts: number;
  sentEmails: number;
  openRate: number;
  replyRate: number;
  clickRate: number;
  revenue: number;
  createdAt: string;
  lastActivity?: string;
  description?: string;
}

interface CampaignStats {
  totalCampaigns: number;
  activeCampaigns: number;
  totalRevenue: number;
  avgOpenRate: number;
  totalContacts: number;
  totalReplies: number;
}

const AnimatedCounter: React.FC<{ end: number; duration?: number; prefix?: string; suffix?: string; decimals?: number }> = ({ 
  end, 
  duration = 2000, 
  prefix = "", 
  suffix = "",
  decimals = 0
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      const currentCount = progress * end;
      setCount(decimals > 0 ? Number(currentCount.toFixed(decimals)) : Math.floor(currentCount));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, decimals]);

  return (
    <span className="font-bold">
      {prefix}{decimals > 0 ? count.toFixed(decimals) : count.toLocaleString()}{suffix}
    </span>
  );
};

export const CampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<LocalCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [typeFilter, setTypeFilter] = useState<string>('All');
  const [stats, setStats] = useState<CampaignStats>({
    totalCampaigns: 0,
    activeCampaigns: 0,
    totalRevenue: 0,
    avgOpenRate: 0,
    totalContacts: 0,
    totalReplies: 0
  });
  const navigate = useNavigate();

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockCampaigns: LocalCampaign[] = [
        // No campaigns in the mock data to match the screenshot
      ];
      
      setCampaigns(mockCampaigns);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Simulate API call
      const mockStats: CampaignStats = {
        totalCampaigns: 0,
        activeCampaigns: 0,
        totalRevenue: 0,
        avgOpenRate: 0,
        totalContacts: 0,
        totalReplies: 0
      };
      setStats(mockStats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  useEffect(() => {
    loadCampaigns();
    loadStats();
  }, []);

  const handleCreateCampaign = () => {
    navigate('/campaigns/create');
  };

  const handleViewCampaign = (campaignId: string) => {
    navigate(`/campaigns/${campaignId}`);
  };

  const handleEditCampaign = (campaignId: string) => {
    navigate(`/campaigns/${campaignId}/edit`);
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         campaign.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'All' || campaign.status.toLowerCase() === statusFilter.toLowerCase();
    const matchesType = typeFilter === 'All' || campaign.type.toLowerCase() === typeFilter.toLowerCase();
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const statusCounts = {
    All: campaigns.length,
    Active: campaigns.filter(c => c.status === 'active').length,
    Paused: campaigns.filter(c => c.status === 'paused').length,
    Draft: campaigns.filter(c => c.status === 'draft').length,
    Completed: campaigns.filter(c => c.status === 'completed').length,
  };

    return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
                                                    </div>

      <div className="relative max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-2">
              Campaign Sequences
            </h1>
            <p className="text-xl text-neutral-400">
              Manage and track all your outreach campaigns
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button 
              variant="outline"
              size="lg"
              className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
            >
              <BarChart3 className="w-5 h-5 mr-2" />
              Analytics Dashboard
            </Button>
            <Button 
              variant="outline"
              size="lg"
              className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
            >
              <Download className="w-5 h-5 mr-2" />
              Export Data
            </Button>
            <Button 
              size="lg"
              onClick={handleCreateCampaign}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Campaign
            </Button>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <MetricCard
            title="Total Campaigns"
            value={stats.totalCampaigns}
            change="+23% this month"
            icon={Rocket}
            gradient="bg-gradient-to-r from-blue-500 to-cyan-500"
            description="All campaign sequences"
          />
          <MetricCard
            title="Active Campaigns"
            value={stats.activeCampaigns}
            change="+5.2% vs last month"
            icon={Play}
            gradient="bg-gradient-to-r from-green-500 to-emerald-500"
            description="Currently running"
          />
          <MetricCard
            title="Total Revenue"
            value={`$${stats.totalRevenue.toLocaleString()}`}
            change="+28% this quarter"
            icon={DollarSign}
            gradient="bg-gradient-to-r from-purple-500 to-pink-500"
            description="Generated from campaigns"
          />
          <MetricCard
            title="Avg Open Rate"
            value={`${stats.avgOpenRate.toFixed(1)}%`}
            change="+12% improvement"
            icon={Mail}
            gradient="bg-gradient-to-r from-orange-500 to-red-500"
            description="Across all email campaigns"
          />
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 mb-8 shadow-xl"
        >
          <div className="flex flex-col space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search campaigns..."
                className="w-full pl-10 pr-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
                />
              </div>

            {/* Filter Tabs */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-6">
              {/* Status Filter */}
              <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-xl p-1">
                {Object.entries(statusCounts).map(([status, count]) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={cn(
                      'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                      statusFilter === status
                        ? 'bg-blue-500 text-white shadow-lg'
                        : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                    )}
                  >
                    {status}
                    {count > 0 && (
                      <span className="ml-2 text-xs bg-neutral-600 text-neutral-300 px-1.5 py-0.5 rounded-full">
                        {count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Type Filter */}
              <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-xl p-1">
                {['All', 'Email', 'LinkedIn', 'Multi-Channel'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setTypeFilter(type)}
                    className={cn(
                      'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                      typeFilter === type
                        ? 'bg-purple-500 text-white shadow-lg'
                        : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                    )}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Campaign Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-neutral-400">Loading campaigns...</p>
            </div>
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <Target className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-3xl font-semibold text-white mb-4">No campaigns found</h3>
            <p className="text-neutral-400 mb-8 max-w-md mx-auto">
              {searchQuery 
                ? 'Try adjusting your search criteria or filters' 
                : 'Start by creating your first campaign to reach your customers'
              }
            </p>
            <Button
              size="lg"
              onClick={handleCreateCampaign}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              <Plus className="w-6 h-6 mr-3" />
              Create Campaign
            </Button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
          >
            {/* Campaign cards would go here when there are campaigns */}
          </motion.div>
        )}
      </div>
    </div>
  );
}; 